import {
	Alert,
	Checkbox,
	Divider,
	Flex,
	Form,
	Input,
	InputNumber,
	Select,
	Tooltip,
	Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useEnvironments } from "@/common/artemis-operations/environments";
import {
	useCreateSignal,
	useSignalCandidates,
	useSignalSources,
} from "@/common/artemis-operations/signals";
import { AwsRegionSelect } from "@/common/aws";
import {
	ERROR_SIGNALS_ADD_TITLE,
	SIGNALS_ADDED_TITLE,
} from "@/common/messages";
import {
	type Deployment,
	EnvironmentProvider,
	type Signal,
	type SignalCandidate,
	type SignalConfigField,
	SignalKind,
	SignalSource,
	type SignalState,
	ThresholdDirection,
} from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { CustomEmpty, FullSpin } from "@/components/base";
import { EnvironmentProviderLabelMap } from "@/components/environments/environment-badges";
import { SegmentedSelection } from "@/components/segmented-selection";
import { Wizard } from "@/components/wizard";
import { SignalStateBadge } from "./signal-badges";

type Scope = "deployment" | "environment" | "project";

/** "an AWS", "a Google Cloud": the article goes by the first letter's sound, close enough for provider names. */
const withArticle = (word: string) =>
	`${/^[aeiou]/i.test(word) ? "an" : "a"} ${word}`;

/** "Alarm ARN or name" → "alarm ARN or name", keeping acronyms intact. */
const lowerFirst = (text: string) =>
	text.charAt(0).toLowerCase() + text.slice(1);

const SCOPE_OPTIONS: Array<{
	value: Scope;
	label: string;
	description: string;
}> = [
	{
		value: "deployment",
		label: "A deployment",
		description: "Watches one deployment and is shown next to it",
	},
	{
		value: "environment",
		label: "An environment",
		description: "Watches an account or environment as a whole",
	},
	{
		value: "project",
		label: "The whole project",
		description: "Watches something that is not in any environment",
	},
];

/** Form field name for a source config key; prefixed so it can't collide with the wizard's own fields. */
const configField = (key: string) => `config_${key}`;

/** A checkbox list of what to add, each with its current state when the source gives one. Works as a Form.Item control. */
function CandidatePicker({
	candidates,
	existing,
	value = [],
	onChange,
}: {
	candidates: SignalCandidate[];
	existing: Set<string>;
	value?: string[];
	onChange?: (value: string[]) => void;
}) {
	if (candidates.length === 0) {
		return <CustomEmpty description="Matches here" />;
	}
	// Metrics come by the hundred, so they are grouped under their CloudWatch namespace (the
	// source puts it in config). Sources without one (alarms, pools, cost) stay one flat list.
	const groups = new Map<string, SignalCandidate[]>();
	for (const c of candidates) {
		const ns = c.config?.find((f) => f.key === "namespace")?.value ?? "";
		groups.set(ns, [...(groups.get(ns) ?? []), c]);
	}
	const ordered = [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
	return (
		<Checkbox.Group
			value={value}
			onChange={(v) => onChange?.(v as string[])}
			style={{ width: "100%" }}
		>
			<Flex vertical gap={8} style={{ width: "100%" }}>
				{ordered.map(([ns, items]) => (
					<Flex key={ns} vertical gap={8} style={{ width: "100%" }}>
						{ns && (
							<Typography.Text strong type="secondary">
								{ns}
							</Typography.Text>
						)}
						{items.map((c) => {
							const added = existing.has(c.externalId);
							return (
								<Flex
									key={c.externalId}
									align="center"
									justify="space-between"
									gap={8}
									style={{ paddingLeft: ns ? 12 : 0 }}
								>
									<Tooltip
										title={added ? "Already added to this project" : undefined}
									>
										<Checkbox value={c.externalId} disabled={added}>
											<Typography.Text type={added ? "secondary" : undefined}>
												{c.name}
											</Typography.Text>
										</Checkbox>
									</Tooltip>
									{c.status && (
										<SignalStateBadge state={c.status.state as SignalState} />
									)}
								</Flex>
							);
						})}
					</Flex>
				))}
			</Flex>
		</Checkbox.Group>
	);
}

/**
 * Adds signals to a project: choose the source and what they belong to (a deployment or the whole
 * project), then pick from what the source has — for CloudWatch, the alarms in the environment's
 * region — or, for sources that can be named by hand, type their ids (CloudWatch: ARN or name).
 */
export function SignalCreateWizard({
	open,
	onClose,
	projectId,
	workspaceId,
	deployments,
	existingSignals,
	initialDeploymentId,
}: {
	open: boolean;
	onClose: () => void;
	projectId: string;
	workspaceId: string;
	deployments: Deployment[];
	existingSignals: Signal[];
	/** Pre-selects this deployment as the scope, e.g. when opened from its drawer. */
	initialDeploymentId?: string;
}) {
	const [form] = Form.useForm();
	const notify = useNotify();
	const createSignal = useCreateSignal();
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");
	const { data: sources } = useSignalSources(workspaceId);
	const { data: environments } = useEnvironments(workspaceId);

	const scope: Scope | undefined = Form.useWatch("scope", form);
	const deploymentId: string | undefined = Form.useWatch("deploymentId", form);
	const environmentId: string | undefined = Form.useWatch(
		"environmentId",
		form,
	);
	const region: string | undefined = Form.useWatch("region", form);
	const source: SignalSource =
		Form.useWatch("source", form) ?? SignalSource.CLOUD_WATCH;

	// Everything below follows the chosen source: CloudWatch reads through AWS environments, an
	// Azure or Google Cloud source would read through its own provider's.
	const sourceInfo = sources?.find((s) => s.source === source);
	const provider = sourceInfo?.environmentProvider ?? undefined;
	const providerLabel = provider ? EnvironmentProviderLabelMap[provider] : "";
	const eligibleEnvironments = (environments ?? []).filter(
		(e) => !!provider && e?.provider === provider,
	);
	const eligibleDeployments = deployments.filter((d) =>
		eligibleEnvironments.some((e) => e?.id === d.environmentId),
	);
	// Regions are an AWS notion; other providers' sources list across the whole environment.
	const hasRegions = provider === EnvironmentProvider.AWS;
	const deployment = eligibleDeployments.find((d) => d.id === deploymentId);
	const readEnvironmentId =
		scope === "deployment"
			? deployment?.environmentId
			: scope === "environment"
				? environmentId
				: undefined;
	const readRegion = !hasRegions
		? undefined
		: scope === "deployment"
			? (deployment?.config?.region ?? undefined)
			: region;

	const candidates = useSignalCandidates(
		readEnvironmentId
			? {
					environmentId: readEnvironmentId,
					source,
					region: readRegion,
					search: search.trim() || undefined,
				}
			: undefined,
	);
	const existing = new Set(existingSignals.map((s) => s.externalId));
	const isMetric = sourceInfo?.kind === SignalKind.METRIC;
	const configFields: SignalConfigField[] = sourceInfo?.configFields ?? [];
	const kindNoun = isMetric ? "metric" : "alarm";

	// What was picked or typed belongs to the previous source; start the second step afresh.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset on a source change only.
	useEffect(() => {
		form.setFieldsValue({ externalIds: [], manualIds: [] });
		setSearch("");
	}, [source]);

	/** The settings typed into the config fields, empty ones dropped. */
	const formConfig = (): Record<string, string> =>
		Object.fromEntries(
			configFields
				.map((f) => [f.key, form.getFieldValue(configField(f.key))])
				.filter(([, v]) => v != null && String(v).trim() !== "")
				.map(([k, v]) => [k, String(v).trim()]),
		);

	/** True when the config fields alone describe a signal: something filled, every required field set. */
	const configDescribesOne = () => {
		const config = formConfig();
		return (
			Object.keys(config).length > 0 &&
			configFields.filter((f) => f.required).every((f) => config[f.key])
		);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: seed once per opening, once the sources are known; the deployment list is stable while the wizard is open.
	useEffect(() => {
		if (!open || !sources) return;
		form.resetFields();
		setSearch("");
		form.setFieldsValue({
			source: SignalSource.CLOUD_WATCH,
			scope:
				initialDeploymentId || eligibleDeployments.length > 0
					? "deployment"
					: provider
						? "environment"
						: "project",
			deploymentId: initialDeploymentId ?? eligibleDeployments[0]?.id,
		});
	}, [open, sources, initialDeploymentId, form]);

	const close = () => {
		onClose();
		form.resetFields();
	};

	const handleComplete = async () => {
		// The form already shows what is missing; nothing more to do on a failed validation.
		const values = await form.validateFields().catch(() => undefined);
		if (!values) return;
		const config = formConfig();
		const toList = (c: Record<string, string>) =>
			Object.entries(c).map(([key, value]) => ({ key, value }));
		const picked = (candidates.data ?? []).filter((c) =>
			((values.externalIds as string[] | undefined) ?? []).includes(
				c.externalId,
			),
		);
		const manual = (values.manualIds as string[] | undefined) ?? [];
		// Each pick keeps its own config (which metric, which pool); typed settings refine it.
		const requests = [
			...picked.map((c) => ({
				externalId: c.externalId,
				name: isMetric ? c.name : null,
				config: toList({
					...Object.fromEntries(
						(c.config ?? []).map((kv) => [kv.key, kv.value]),
					),
					...config,
				}),
			})),
			...manual.map((externalId) => ({
				externalId,
				name: null,
				config: toList(config),
			})),
			...(picked.length === 0 && manual.length === 0
				? [{ externalId: null, name: null, config: toList(config) }]
				: []),
		];
		const thresholds =
			isMetric &&
			(values.threshold_warning != null || values.threshold_critical != null)
				? {
						warning: values.threshold_warning ?? null,
						critical: values.threshold_critical ?? null,
						direction: values.threshold_direction ?? ThresholdDirection.ABOVE,
					}
				: null;
		setLoading(true);
		const results = await Promise.allSettled(
			requests.map((r) =>
				createSignal.mutateAsync({
					projectId,
					source,
					externalId: r.externalId,
					name: r.name,
					config: r.config,
					thresholds,
					deploymentId:
						values.scope === "deployment" ? values.deploymentId : null,
					environmentId:
						values.scope === "environment" ? values.environmentId : null,
					description: values.description?.trim() || null,
				}),
			),
		);
		setLoading(false);
		const failures = results.filter(
			(r): r is PromiseRejectedResult => r.status === "rejected",
		);
		const added = results.length - failures.length;
		if (added > 0) {
			notify.success(
				SIGNALS_ADDED_TITLE,
				`${added} signal${added === 1 ? "" : "s"} added.`,
			);
		}
		if (failures.length > 0) {
			notify.error(
				ERROR_SIGNALS_ADD_TITLE,
				failures.map((f) => (f.reason as Error).message).join("\n"),
			);
			return;
		}
		close();
	};

	// Sources grouped by the provider they read through (AWS, Azure, …), providers and sources
	// both alphabetical; sources without a provider (workspace apps) go last under "Other".
	const byProvider = new Map<string, typeof sources>();
	for (const s of sources ?? []) {
		const label = s.environmentProvider
			? EnvironmentProviderLabelMap[s.environmentProvider]
			: "Other";
		byProvider.set(label, [...(byProvider.get(label) ?? []), s]);
	}
	const sourceGroups = [...byProvider.entries()]
		.sort(([a], [b]) =>
			a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b),
		)
		.map(([label, group]) => ({
			label,
			options: (group ?? [])
				.sort((a, b) => a.displayName.localeCompare(b.displayName))
				.map((s) => ({
					value: s.source as SignalSource,
					label: s.displayName,
					disabled: !s.available,
					title: s.available
						? undefined
						: (s.unavailableReason ?? "Not available in this workspace"),
				})),
		}));

	const manualLabel = sourceInfo?.externalIdLabel;

	const pages = [
		{
			id: "scope",
			title: "Scope",
			fields: ["source", "scope", "deploymentId", "environmentId"],
			content: (
				<Flex vertical gap={16} style={{ width: "100%" }}>
					<Form.Item name="source" label="Source" rules={[{ required: true }]}>
						<Select
							options={sourceGroups}
							placeholder="Select a source"
							showSearch
							optionFilterProp="label"
						/>
					</Form.Item>
					<Form.Item
						name="scope"
						label="Belongs to"
						rules={[{ required: true }]}
					>
						<SegmentedSelection
							options={SCOPE_OPTIONS.map((o) => ({
								...o,
								// A source that reads through an environment can't be project-wide.
								disabled:
									(o.value === "deployment" &&
										eligibleDeployments.length === 0) ||
									(o.value === "project" && !!provider),
							}))}
						/>
					</Form.Item>
					{scope === "deployment" && (
						<Form.Item
							name="deploymentId"
							label="Deployment"
							rules={[{ required: true, message: "Choose a deployment" }]}
						>
							<Select
								placeholder="Select a deployment"
								options={eligibleDeployments.map((d) => ({
									value: d.id,
									label: d.name,
								}))}
							/>
						</Form.Item>
					)}
					{scope === "environment" && (
						<>
							<Form.Item
								name="environmentId"
								label="Environment"
								rules={[{ required: true, message: "Choose an environment" }]}
								extra="The account it is read from."
							>
								<Select
									placeholder={`Select ${withArticle(providerLabel)} environment`}
									options={eligibleEnvironments.map((e) => ({
										value: e?.id,
										label: e?.name,
									}))}
								/>
							</Form.Item>
							{hasRegions && (
								<Form.Item
									name="region"
									label="Region"
									extra="Leave empty for the environment's default region."
								>
									<AwsRegionSelect allowClear />
								</Form.Item>
							)}
						</>
					)}
					{sources && provider && eligibleEnvironments.length === 0 && (
						<Alert
							type="info"
							title={`Add ${withArticle(providerLabel)} environment first`}
							description={`${sourceInfo?.displayName} signals are read through ${withArticle(providerLabel)} environment's connection. Add one to this workspace, then come back to add signals.`}
						/>
					)}
				</Flex>
			),
		},
		{
			id: "pick",
			title: isMetric ? "Metrics" : "Alarms",
			content: (
				<Flex vertical gap={16} style={{ width: "100%" }}>
					<Input.Search
						placeholder={
							source === SignalSource.CLOUD_WATCH_METRIC
								? "Filter by metric name, or a namespace like AWS/Lambda"
								: "Filter by name prefix"
						}
						allowClear
						onSearch={setSearch}
					/>
					{candidates.isError && (
						<Alert
							type="warning"
							title={`Couldn't list ${kindNoun}s`}
							description={(candidates.error as Error).message}
						/>
					)}
					{candidates.isFetching ? (
						<FullSpin />
					) : (
						!candidates.isError && (
							<Form.Item
								name="externalIds"
								rules={[
									{
										// One rule for every way to add: pick, type an id, or describe it in settings.
										validator: async (_, picked: string[] | undefined) => {
											const manual: string[] =
												form.getFieldValue("manualIds") ?? [];
											if (
												(picked ?? []).length > 0 ||
												manual.length > 0 ||
												configDescribesOne()
											)
												return;
											const ways = [
												`pick at least one ${kindNoun}`,
												manualLabel && `add one by ${lowerFirst(manualLabel)}`,
												configFields.length > 0 && "fill in the settings below",
											].filter(Boolean);
											const message = ways.join(", or ");
											throw new Error(
												message.charAt(0).toUpperCase() + message.slice(1),
											);
										},
									},
								]}
							>
								<CandidatePicker
									candidates={candidates.data ?? []}
									existing={existing}
								/>
							</Form.Item>
						)
					)}
					{/* Only sources that can be named by hand get a free-text field — e.g. a
					    CloudWatch alarm by ARN or name. */}
					{manualLabel && (
						<Form.Item
							name="manualIds"
							label={`Or add by ${lowerFirst(manualLabel)}`}
						>
							<Select
								mode="tags"
								open={false}
								placeholder={manualLabel}
								onChange={() => form.validateFields(["externalIds"])}
							/>
						</Form.Item>
					)}
					{configFields.length > 0 && (
						<>
							<Divider style={{ margin: 0 }}>Settings</Divider>
							{configFields.map((f) => (
								<Form.Item
									key={f.key}
									name={configField(f.key)}
									label={f.label}
									initialValue={f.defaultValue ?? undefined}
									extra={
										f.required
											? "Needed when nothing is picked above."
											: undefined
									}
								>
									{f.options?.length ? (
										<Select
											allowClear
											placeholder={f.placeholder ?? f.defaultValue ?? undefined}
											options={f.options.map((o) => ({ value: o, label: o }))}
										/>
									) : (
										<Input
											placeholder={f.placeholder ?? undefined}
											onChange={() => form.validateFields(["externalIds"])}
										/>
									)}
								</Form.Item>
							))}
						</>
					)}
					{isMetric && (
						<>
							<Divider style={{ margin: 0 }}>Thresholds (optional)</Divider>
							<Typography.Paragraph style={{ margin: 0 }}>
								Without thresholds a metric is shown as a number and doesn't
								count towards the project's health.
							</Typography.Paragraph>
							<Flex gap={12} wrap>
								<Form.Item
									name="threshold_warning"
									label="Warning at"
									style={{ flex: "1 1 140px" }}
								>
									<InputNumber style={{ width: "100%" }} />
								</Form.Item>
								<Form.Item
									name="threshold_critical"
									label="Critical at"
									style={{ flex: "1 1 140px" }}
								>
									<InputNumber style={{ width: "100%" }} />
								</Form.Item>
								<Form.Item
									name="threshold_direction"
									label="Bad when"
									initialValue={ThresholdDirection.ABOVE}
									style={{ flex: "1 1 140px" }}
								>
									<Select
										options={[
											{ value: ThresholdDirection.ABOVE, label: "At or above" },
											{ value: ThresholdDirection.BELOW, label: "At or below" },
										]}
									/>
								</Form.Item>
							</Flex>
						</>
					)}
					<Form.Item
						name="description"
						label="Description"
						extra={`Why it matters, or what to do when it ${isMetric ? "crosses a threshold" : "fires"}. Added to every signal in this step.`}
						rules={[{ max: 2000 }]}
					>
						<Input.TextArea
							rows={3}
							placeholder="e.g. Checkout errors — page the payments on-call if this fires for more than 10 minutes"
						/>
					</Form.Item>
				</Flex>
			),
		},
	];

	return (
		<Wizard
			open={open}
			title="Add signals"
			completeLabel="Add"
			form={form}
			loading={loading}
			onComplete={handleComplete}
			onCancel={close}
			pages={pages}
		/>
	);
}
